import React, { useState } from 'react'
import ReactModal from 'react-modal'

// Request to close: (1) if the user has confirmed team-ups, pick who they
// teamed up with (or skip), then (2) confirm a contract was signed.
// Same flow and callbacks as before - only the look and wording changed.
const clean = (v) => (v && String(v).trim() && !['null', 'undefined'].includes(String(v).trim().toLowerCase()) ? String(v).trim() : '');
const personName = (u) => [clean(u?.firstName), clean(u?.lastName)].filter(Boolean).join(' ') || 'Name not provided';

const BTN = 'font-plexsans font-semibold text-[13px] leading-none rounded-md py-3 px-[18px] cursor-pointer w-full sm:w-auto text-center';
const BTN_PRIMARY = `${BTN} text-ivory bg-brass border border-brass hover:bg-brassDark hover:border-brassDark`;
const BTN_SECONDARY = `${BTN} text-navy bg-white border border-navy/[.2] hover:bg-navy/[.04]`;

function Row({ label, children }) {
  return (
    <div className='flex justify-between gap-4 py-[11px] border-b border-navy/[.08] last:border-b-0'>
      <span className='font-plexsans text-[13.5px] leading-[1.4] text-navy/[.6] flex-none'>{label}</span>
      <span className='font-plexsans font-semibold text-[13.5px] leading-[1.4] text-navy text-right min-w-0 [overflow-wrap:anywhere]'>{children}</span>
    </div>
  );
}

function CloseCaseModal({uploadNew, onClose, onSubmit, caseDetails, confirmedTeamUp, teamUpObj, setTeamUpObj, userInfo}) {
  const hasTeamUps = !!(confirmedTeamUp && confirmedTeamUp.length > 0);
  const [currentTab, setCurrentTab] = useState(hasTeamUps ? 0 : 1);

  const d = caseDetails || {};
  const funder = !!userInfo?.isUserFunder;
  const partner = funder ? 'attorney' : 'funder';
  const contract = funder ? 'Litigation funding contract' : 'Client representation contract';
  const clientName = [clean(d.firstName), clean(d.lastName)].filter(Boolean).join(' ');
  const client = d.isOwnerAnonymous && !clientName ? 'Anonymous' : (clientName || 'Name not provided');

  // the other person in each confirmed team-up
  const options = (confirmedTeamUp || []).map((invite) => {
    const mine = invite.fromUser === userInfo?._id;
    const other = mine ? invite.toUser : invite.fromUser;
    const value = mine
      ? { toUserId: invite.toUser?._id, fromUserId: invite.fromUser }
      : { fromUserId: invite.fromUser?._id, toUserId: invite.toUser };
    const company = clean(other?.company);
    return { key: other?._id, label: personName(other) + (company ? ', ' + company : ''), value: JSON.stringify(value) };
  });
  const selected = teamUpObj ? options.find((o) => o.value === JSON.stringify(teamUpObj)) : null;

  return (
    <ReactModal
    shouldCloseOnEsc = {true}
    shouldCloseOnOverlayClick = {true}
    onRequestClose={onClose}
    isOpen = {uploadNew}
    style={{
        overlay: { zIndex: 50, backgroundColor: 'rgba(20,33,61,.45)' },
        content: {
            zIndex: 50, top: '50%', left: '50%', right: 'auto', bottom: 'auto', marginRight: '-50%', transform: 'translate(-50%, -50%)',
            width: 'calc(100vw - 32px)', maxWidth: '520px', maxHeight: 'calc(100vh - 64px)', padding: 0, border: 'none', borderRadius: '8px',
            background: '#f7f4ed', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px -20px rgba(20,33,61,.5)',
        }
      }}
    >
      <div className='font-plexsans flex-none flex items-start justify-between gap-4 py-[18px] px-[22px] bg-white border-b border-navy/[.1]'>
        <div className='flex flex-col gap-1.5 min-w-0'>
          <span className='font-plexmono font-medium text-[11px] leading-none tracking-[.08em] uppercase text-brass'>
            Request to close{d.caseNumber ? ' · ' + d.caseNumber : ''}{hasTeamUps ? ` · Step ${currentTab === 0 ? 1 : 2} of 2` : ''}
          </span>
          <span className='font-spectral font-normal text-[19px] leading-[1.25] text-navy'>
            {currentTab === 0 ? `Did you team up with ${funder ? 'an attorney' : 'a funder'}?` : 'Have you signed a contract?'}
          </span>
        </div>
        <button type="button" onClick={onClose} title="Close" className='flex-none flex bg-transparent border-0 cursor-pointer text-navy p-0.5'>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M6 6l12 12M18 6L6 18"/></svg>
        </button>
      </div>

      {currentTab === 0 && hasTeamUps &&
        <>
          <div className='flex-1 min-h-0 overflow-y-auto pt-5 px-[22px] pb-5 font-plexsans flex flex-col gap-4'>
            <p className='text-[13.5px] leading-[1.55] text-navy/[.75] m-0'>
              If you worked on this case with a {partner}, select them below. If not, continue without a team-up.
            </p>
            <label className='flex flex-col gap-2'>
              <span className='font-plexsans font-semibold text-[11px] leading-none tracking-[.08em] uppercase text-navy/[.55]'>Teamed up with</span>
              <select
                value={selected ? selected.value : ''}
                onChange={(e) => e.target.value ? setTeamUpObj(JSON.parse(e.target.value)) : setTeamUpObj(null)}
                className='w-full h-11 px-3 font-plexsans text-[13.5px] text-navy bg-white border border-navy/[.2] rounded-md cursor-pointer focus:outline-none focus:border-brass'
              >
                <option value=''>Select {funder ? 'an attorney' : 'a funder'}</option>
                {options.map((o) => <option key={o.key || o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
          </div>
          <div className='flex-none flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5 py-4 px-[22px] bg-white border-t border-navy/[.1]'>
            <button type="button" className={BTN_SECONDARY} onClick={() => { setTeamUpObj(null); setCurrentTab(1); }}>
              Continue without team-up
            </button>
            <button type="button" disabled={!teamUpObj} onClick={() => teamUpObj && setCurrentTab(1)}
              className={`${BTN_PRIMARY} disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-brass`}>
              Continue →
            </button>
          </div>
        </>
      }

      {currentTab === 1 &&
        <>
          <div className='flex-1 min-h-0 overflow-y-auto pt-5 px-[22px] pb-5 font-plexsans flex flex-col gap-4'>
            <p className='text-[13.5px] leading-[1.55] text-navy/[.75] m-0'>
              You can request to close this case only after you and the client have signed a contract.
            </p>
            <div className='bg-white border border-navy/[.12] rounded-md px-4'>
              <Row label="Client">{client}</Row>
              <Row label="Contract needed">{contract}</Row>
              {selected && <Row label="Teamed up with">{selected.label}</Row>}
            </div>
            <p className='text-[12.5px] leading-[1.5] text-navy/[.6] m-0'>
              Once you request to close, other {funder ? 'funders' : 'attorneys'} can no longer request this case.
            </p>
          </div>
          <div className='flex-none flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2.5 py-4 px-[22px] bg-white border-t border-navy/[.1]'>
            {hasTeamUps &&
              <button type="button" onClick={() => setCurrentTab(0)}
                className='sm:mr-auto font-plexsans font-semibold text-[13px] leading-none text-brass bg-transparent border-0 cursor-pointer py-3 px-0 hover:text-brassDark'>
                ← Back
              </button>}
            <button type="button" className={BTN_SECONDARY} onClick={onClose}>Not yet</button>
            <button type="button" className={BTN_PRIMARY} onClick={() => onSubmit()}>Yes, contract signed</button>
          </div>
        </>
      }
    </ReactModal>
  )
}

export default CloseCaseModal;
